"use client";

import { useLingui } from "@lingui/react";
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type AnalysisTarget,
  getAnalysisTargetKey,
} from "@/lib/ai/analysis-target";
import {
  type AnalysisTargetMentionAnchor,
  type AnalysisTargetMentionMatch,
  findAnalysisTargetMention,
  getAnalysisTargetMentionAnchor,
} from "./analysis-target-mention";
import {
  type AnalysisTargetMentionInstance,
  getAnalysisTargetMentionInputs,
  getAnalysisTargetMentionTokenRanges,
  getMentionedAnalysisTargets,
  getMentionTokenCursorMoveTarget,
  getMentionTokenDeletionRange,
  getMentionTokenSelectionDeletionRange,
  insertAnalysisTargetMention,
  isCursorInsideMentionToken,
  reconcileAnalysisTargetMentionInstances,
  removeAnalysisTargetMentionInstances,
} from "./analysis-target-mention-token";
import {
  type AnalysisTargetOption,
  type AnalysisTargetOptionsState,
  filterAnalysisTargetOptionGroups,
  flattenAnalysisTargetOptionGroups,
} from "./analysis-target-options";
import {
  __resetSharedAnalysisTargetPageContextCacheForTest,
  useAnalysisTargetPageContextSync,
} from "./use-analysis-target-page-context";

type UseAnalysisTargetMentionInputParams = {
  isInteractive: boolean;
  input: string;
  setInput: (value: string) => void;
  pathname: string;
  /**
   * `false` のときは親 owner が page-context sync を担当する。
   */
  pageContextSyncEnabled?: boolean;
  /**
   * 親 owner が持つ suppress handler を使う場合に上書きする。
   */
  externalSuppressPageContextAutoSyncOnCurrentPath?: () => void;
  optionsState: AnalysisTargetOptionsState;
  selectedTargets: AnalysisTarget[];
  hasRemovableSelectedTarget: boolean;
  setPageSelectedTarget: (target: AnalysisTarget | null) => void;
  addSelectedTarget?: (target: AnalysisTarget) => void;
  removeLastSelectedTarget: () => void;
};

/**
 * テスト間で page context 共有キャッシュがリークしないようにするリセット関数。
 */
export function __resetSharedAnalysisTargetCacheForTest(): void {
  __resetSharedAnalysisTargetPageContextCacheForTest();
}

/**
 * `@` メンション入力の状態とイベント処理を 1 つにまとめる。
 *
 * 契約:
 * - ページコンテキスト同期は専用 hook（`useAnalysisTargetPageContextSync`）に委譲する。
 * - inline mention は text とは別に instance 配列で保持し、送信時の identity を失わない。
 */
export function useAnalysisTargetMentionInput({
  isInteractive,
  input,
  setInput,
  pathname,
  pageContextSyncEnabled = true,
  externalSuppressPageContextAutoSyncOnCurrentPath,
  optionsState,
  selectedTargets,
  hasRemovableSelectedTarget,
  setPageSelectedTarget,
  addSelectedTarget,
  removeLastSelectedTarget,
}: UseAnalysisTargetMentionInputParams) {
  const { i18n } = useLingui();
  const [mentionMatch, setMentionMatch] =
    useState<AnalysisTargetMentionMatch | null>(null);
  const [mentionAnchor, setMentionAnchor] =
    useState<AnalysisTargetMentionAnchor | null>(null);
  const [mentionActiveOptionKey, setMentionActiveOptionKey] = useState<
    string | null
  >(null);
  const [mentionInstances, setMentionInstances] = useState<
    AnalysisTargetMentionInstance[]
  >([]);
  const [textareaElement, setTextareaElement] =
    useState<HTMLTextAreaElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const lastCommittedInputRef = useRef(input);

  const {
    suppressPageContextAutoSyncOnCurrentPath:
      localSuppressPageContextAutoSyncOnCurrentPath,
  } = useAnalysisTargetPageContextSync({
    shouldSyncPageContext: pageContextSyncEnabled,
    pathname,
    options: optionsState.options,
    selectedTargets,
    setPageSelectedTarget,
  });
  const suppressPageContextAutoSyncOnCurrentPath =
    externalSuppressPageContextAutoSyncOnCurrentPath ??
    localSuppressPageContextAutoSyncOnCurrentPath;

  const mentionMenuOpen =
    isInteractive && mentionMatch !== null && mentionAnchor !== null;
  const mentionFilteredGroups = useMemo(
    () =>
      mentionMatch
        ? filterAnalysisTargetOptionGroups(
            optionsState.groupedOptions,
            mentionMatch.query,
            { locale: i18n.locale },
          )
        : [],
    [i18n.locale, mentionMatch, optionsState.groupedOptions],
  );
  const mentionOptions = useMemo(
    () => flattenAnalysisTargetOptionGroups(mentionFilteredGroups),
    [mentionFilteredGroups],
  );
  const mentionOptionMap = useMemo(
    () => new Map(mentionOptions.map((option) => [option.key, option])),
    [mentionOptions],
  );
  const mentionTokenRanges = useMemo(
    () => getAnalysisTargetMentionTokenRanges(mentionInstances),
    [mentionInstances],
  );
  const mentionInputs = useMemo(
    () => getAnalysisTargetMentionInputs(input, mentionInstances),
    [input, mentionInstances],
  );
  const mentionedTargets = useMemo(
    () => getMentionedAnalysisTargets(mentionInstances),
    [mentionInstances],
  );

  const bindTextarea = useCallback(
    (nextElement: HTMLTextAreaElement | null) => {
      textareaRef.current = nextElement;
      setTextareaElement((previous) =>
        previous === nextElement ? previous : nextElement,
      );
    },
    [],
  );

  /**
   * 候補 UI が閉じるタイミングで、関連 state を一括で初期化する。
   */
  const resetMentionState = useCallback(() => {
    setMentionMatch(null);
    setMentionAnchor(null);
    setMentionActiveOptionKey(null);
  }, []);

  /**
   * inline mention instance を全削除する。
   *
   * 送信後や外部入力差し替え時に、本文との整合を速やかに取り戻すために使う。
   */
  const clearMentionInstances = useCallback(() => {
    setMentionInstances([]);
  }, []);

  const syncMentionMatch = useCallback(
    (
      nextInput: string,
      element: HTMLTextAreaElement | null,
      nextTokenRanges: AnalysisTargetMentionInstance[],
    ) => {
      if (!isInteractive || !element) {
        resetMentionState();
        return;
      }

      const cursor = element.selectionStart ?? nextInput.length;
      const nextMatch = findAnalysisTargetMention(nextInput, cursor);
      if (!nextMatch) {
        resetMentionState();
        return;
      }

      if (isCursorInsideMentionToken(cursor, nextTokenRanges)) {
        // 既存メンショントークン上ではサジェスト表示を抑制し、塊操作を優先する。
        resetMentionState();
        return;
      }

      setMentionMatch(nextMatch);
      setMentionAnchor(
        getAnalysisTargetMentionAnchor(element, nextMatch.start + 1),
      );
    },
    [isInteractive, resetMentionState],
  );

  const commitInputChange = useCallback(
    (nextInput: string, nextMentions: AnalysisTargetMentionInstance[]) => {
      lastCommittedInputRef.current = nextInput;
      setMentionInstances(nextMentions);
      setInput(nextInput);
    },
    [setInput],
  );

  /**
   * 外部から `input` prop が差し替わった場合でも、mention instance を同じ diff で追従させる。
   *
   * sample prompt クリックや送信後クリアのように hook 外で `setInput` される経路でも、
   * stale な mention range が残らないようにする。
   */
  useEffect(() => {
    if (input === lastCommittedInputRef.current) {
      return;
    }

    setMentionInstances((previousMentions) =>
      reconcileAnalysisTargetMentionInstances({
        previousValue: lastCommittedInputRef.current,
        nextValue: input,
        mentions: previousMentions,
      }),
    );
    lastCommittedInputRef.current = input;
  }, [input]);

  /**
   * 入力値更新とキャレット再配置を 1 か所に集約する。
   *
   * React state 更新後も textarea のフォーカス/選択位置を保ち、
   * mention 候補状態を最新入力へ再同期する。
   */
  const applyInputAndCaret = useCallback(
    (nextInput: string, nextCursor: number) => {
      const nextMentions = reconcileAnalysisTargetMentionInstances({
        previousValue: lastCommittedInputRef.current,
        nextValue: nextInput,
        mentions: mentionInstances,
      });
      commitInputChange(nextInput, nextMentions);

      requestAnimationFrame(() => {
        const element = textareaRef.current;
        if (!element) {
          return;
        }
        element.focus();
        element.setSelectionRange(nextCursor, nextCursor);
        syncMentionMatch(nextInput, element, nextMentions);
      });
    },
    [commitInputChange, mentionInstances, syncMentionMatch],
  );

  const handleMentionTargetSelect = useCallback(
    (target: AnalysisTarget) => {
      if (!mentionMatch) {
        return;
      }

      const inserted = insertAnalysisTargetMention({
        value: input,
        mentions: mentionInstances,
        match: mentionMatch,
        target,
      });

      resetMentionState();
      // `@` は inline token だけでなく送信 scope も追加する。
      // 以後トークン文字列が壊れても、chip 削除までは scope を維持する。
      addSelectedTarget?.(target);
      commitInputChange(inserted.value, inserted.mentions);

      requestAnimationFrame(() => {
        const element = textareaRef.current;
        if (!element) {
          return;
        }
        element.focus();
        element.setSelectionRange(inserted.cursor, inserted.cursor);
        syncMentionMatch(inserted.value, element, inserted.mentions);
      });
    },
    [
      commitInputChange,
      input,
      mentionInstances,
      mentionMatch,
      addSelectedTarget,
      resetMentionState,
      syncMentionMatch,
    ],
  );

  /**
   * 表示 chip と送信 scope を一致させるため、chip 削除時は同 refId の mention を本文から全て外す。
   */
  const removeMentionTarget = useCallback(
    (target: AnalysisTarget) => {
      const removed = removeAnalysisTargetMentionInstances({
        value: input,
        mentions: mentionInstances,
        refId: getAnalysisTargetKey(target),
      });
      if (
        removed.value === input &&
        removed.mentions.length === mentionInstances.length
      ) {
        return;
      }

      commitInputChange(removed.value, removed.mentions);
      resetMentionState();

      requestAnimationFrame(() => {
        const element = textareaRef.current;
        if (!element) {
          return;
        }
        const nextCursor = Math.min(
          element.selectionStart ?? removed.value.length,
          removed.value.length,
        );
        element.focus();
        element.setSelectionRange(nextCursor, nextCursor);
        syncMentionMatch(removed.value, element, removed.mentions);
      });
    },
    [
      commitInputChange,
      input,
      mentionInstances,
      resetMentionState,
      syncMentionMatch,
    ],
  );

  const moveMentionSelection = useCallback(
    (delta: number) => {
      if (mentionOptions.length === 0) {
        return;
      }
      setMentionActiveOptionKey((previousKey) => {
        const currentIndex = previousKey
          ? mentionOptions.findIndex((option) => option.key === previousKey)
          : -1;
        const nextIndex =
          currentIndex >= 0
            ? (currentIndex + delta + mentionOptions.length) %
              mentionOptions.length
            : delta > 0
              ? 0
              : mentionOptions.length - 1;
        return mentionOptions[nextIndex]?.key ?? mentionOptions[0]?.key ?? null;
      });
    },
    [mentionOptions],
  );

  useEffect(() => {
    if (!mentionMenuOpen || mentionOptions.length === 0) {
      setMentionActiveOptionKey(null);
      return;
    }

    setMentionActiveOptionKey((previousKey) => {
      if (previousKey && mentionOptionMap.has(previousKey)) {
        return previousKey;
      }

      return mentionOptions[0]?.key ?? null;
    });
  }, [mentionMenuOpen, mentionOptionMap, mentionOptions]);

  const handleTextareaChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = event.currentTarget.value;
      bindTextarea(event.currentTarget);
      const nextMentions = reconcileAnalysisTargetMentionInstances({
        previousValue: lastCommittedInputRef.current,
        nextValue,
        mentions: mentionInstances,
      });
      commitInputChange(nextValue, nextMentions);
      syncMentionMatch(nextValue, event.currentTarget, nextMentions);
    },
    [bindTextarea, commitInputChange, mentionInstances, syncMentionMatch],
  );

  const handleTextareaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = event.currentTarget;
      bindTextarea(textarea);

      const selectionStart = textarea.selectionStart ?? 0;
      const selectionEnd = textarea.selectionEnd ?? selectionStart;
      const hasCollapsedSelection = selectionStart === selectionEnd;
      const isModifierKeyPressed =
        event.shiftKey || event.altKey || event.metaKey || event.ctrlKey;

      if (event.key === "Escape") {
        resetMentionState();
        return;
      }

      if (
        isInteractive &&
        hasCollapsedSelection &&
        !isModifierKeyPressed &&
        (event.key === "ArrowLeft" || event.key === "ArrowRight")
      ) {
        const nextCursor = getMentionTokenCursorMoveTarget(
          selectionStart,
          mentionTokenRanges,
          event.key === "ArrowLeft" ? "left" : "right",
        );
        if (nextCursor !== null) {
          event.preventDefault();
          textarea.setSelectionRange(nextCursor, nextCursor);
          syncMentionMatch(input, textarea, mentionTokenRanges);
          return;
        }
      }

      if (
        isInteractive &&
        !hasCollapsedSelection &&
        !isModifierKeyPressed &&
        !event.nativeEvent.isComposing &&
        (event.key === "Backspace" || event.key === "Delete")
      ) {
        const selectionDeletionRange = getMentionTokenSelectionDeletionRange(
          input,
          selectionStart,
          selectionEnd,
          mentionTokenRanges,
        );
        if (selectionDeletionRange) {
          event.preventDefault();
          const nextInput = `${input.slice(0, selectionDeletionRange.start)}${input.slice(selectionDeletionRange.end)}`;
          // 部分選択でもメンションに重なったら常に 1 トークン単位で削除する。
          // ここでは inline token だけを消し、scope chip は別 state に残す。
          applyInputAndCaret(nextInput, selectionDeletionRange.start);
          return;
        }
      }

      if (
        isInteractive &&
        hasCollapsedSelection &&
        !isModifierKeyPressed &&
        !event.nativeEvent.isComposing &&
        (event.key === "Backspace" || event.key === "Delete")
      ) {
        const deletionRange = getMentionTokenDeletionRange(
          input,
          selectionStart,
          mentionTokenRanges,
          event.key === "Backspace" ? "backward" : "forward",
        );
        if (deletionRange) {
          event.preventDefault();
          const nextInput = `${input.slice(0, deletionRange.start)}${input.slice(deletionRange.end)}`;
          // Delete / Backspace は token 文字列だけを消し、scope 削除は chip 操作へ寄せる。
          applyInputAndCaret(nextInput, deletionRange.start);
          return;
        }
      }

      if (mentionMenuOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          moveMentionSelection(1);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          moveMentionSelection(-1);
          return;
        }
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          !event.nativeEvent.isComposing
        ) {
          // 候補メニュー表示中の Enter は「選択確定専用」にし、
          // 誤ってチャット送信へ流れないようここで打ち止めにする。
          event.preventDefault();
          const selectedMentionOption: AnalysisTargetOption | undefined =
            mentionActiveOptionKey
              ? mentionOptionMap.get(mentionActiveOptionKey)
              : mentionOptions[0];
          if (selectedMentionOption) {
            handleMentionTargetSelect(selectedMentionOption.target);
          }
          return;
        }
      }

      if (
        isInteractive &&
        (event.key === "Backspace" || event.key === "Delete") &&
        input.length === 0 &&
        hasRemovableSelectedTarget
      ) {
        event.preventDefault();
        suppressPageContextAutoSyncOnCurrentPath();
        removeLastSelectedTarget();
        return;
      }

      if (
        isInteractive &&
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        event.currentTarget.form?.requestSubmit();
      }
    },
    [
      applyInputAndCaret,
      bindTextarea,
      isInteractive,
      handleMentionTargetSelect,
      hasRemovableSelectedTarget,
      input,
      mentionActiveOptionKey,
      mentionMenuOpen,
      mentionOptionMap,
      mentionOptions,
      mentionTokenRanges,
      moveMentionSelection,
      removeLastSelectedTarget,
      resetMentionState,
      suppressPageContextAutoSyncOnCurrentPath,
      syncMentionMatch,
    ],
  );

  const handleTextareaKeyUp = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      bindTextarea(event.currentTarget);
      if (event.key === "Escape") {
        resetMentionState();
        return;
      }
      syncMentionMatch(
        event.currentTarget.value,
        event.currentTarget,
        mentionTokenRanges,
      );
    },
    [bindTextarea, mentionTokenRanges, resetMentionState, syncMentionMatch],
  );

  const handleTextareaClick = useCallback(
    (event: MouseEvent<HTMLTextAreaElement>) => {
      bindTextarea(event.currentTarget);
      syncMentionMatch(
        event.currentTarget.value,
        event.currentTarget,
        mentionTokenRanges,
      );
    },
    [bindTextarea, mentionTokenRanges, syncMentionMatch],
  );

  return {
    mentionMatch,
    mentionAnchor,
    mentionMenuOpen,
    mentionActiveOptionKey,
    mentionInputs,
    mentionedTargets,
    mentionInstances,
    setMentionActiveOptionKey,
    handleMentionTargetSelect,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleTextareaKeyUp,
    handleTextareaClick,
    resetMentionState,
    clearMentionInstances,
    removeMentionTarget,
    suppressPageContextAutoSyncOnCurrentPath,
    textareaElement,
    bindTextarea,
  };
}
